/**
 * notifications/api.ts — the browser's client for the in-platform notification feed
 * (/api/notifications, /api/me/notification-settings). THIS IS A HAND-WRITTEN MIRROR of the server's
 * shapes (db/notification.ts, routes/notifications.ts); it is not generated, so a kind added there
 * and not here reaches no screen while both typechecks stay green. The five personal kinds the
 * backend writes today:
 *
 *   kind 3 — someone commented on / reported an issue on your tool (builder-only).
 *   kind 4 — your tool submission changed publication status (submitter-only, cannot be turned off).
 *   kind 6 — someone replied to a thread post you posted (poster-only).
 *   kind 7 — EITHER the outcome + reason on your own content or support request, OR the outcome of a
 *            report YOU raised on somebody else's content, which carries no reason (cannot be turned
 *            off; two writers, told apart by `subjectKind` — see the payload note below).
 *   kind 8 — your tool first entered the weekly Top 5 (builder-only).
 *
 * Kind 1 (tool_published) is written too but is announced to the community CHANNEL, so its row has
 * `recipient_user_id` NULL and no personal feed query can return it. Kinds 2 and 5 are unbuilt, and
 * likes/follow/badge are permanently banned (NT §8) — see db/notification.ts's module header.
 *
 * Same conventions as community/api.ts: same-origin fetch, the session cookie is the only credential,
 * reuses its generic apiGet/apiPost result union rather than inventing a second one.
 */
import { apiGet, apiPost, apiDelete, type ApiResult } from '../community/api';

/**
 * One notification in the caller's own feed.
 *
 * `payload` is typed `unknown` and NOT as a union of the shapes below, because a union ending in
 * `| unknown` is just `unknown` — TypeScript absorbs every member — so the old union looked like a
 * checked contract while enforcing nothing at all. The renderers narrow it themselves. The shapes the
 * backend actually sends, per kind (ids and governed enums only, NT-F06 — never a third party's name
 * and never body text, with kind 7's `reason` the documented exception):
 *
 *   kind 3 — `{toolId, commentId}` (notifyToolComment) or `{toolId, issueId}` (notifyIssueReport)
 *   kind 4 — `{toolId, reviewId, status}`, status = PublicationStatus ('Approved' | 'Rejected' | …)
 *   kind 6 — `{threadId, postId}` (notifyReply)
 *   kind 7 — TWO WRITERS, told apart by `subjectKind`, and they mean opposite things:
 *            • notifyOutcomeOnYourContent → `{subjectKind: 'flag'|'request'|'comment', subjectId,
 *              status, reason}` PLUS the decider's context ids spread FLAT onto the same object (there
 *              is NO nested `context` key): `{threadId, postId}` for a forum post, `{toolId, commentId}`
 *              for a tool comment, `{toolId}` or nothing for a request. To the person whose OWN content
 *              or request was judged, which is why it may carry the decider's `reason`.
 *            • notifyOutcomeOnYourReport → `{subjectKind: 'report', subjectId: flagId, status:
 *              'remove'|'dismiss', targetKind: 'comment'|'forum_post', targetId}`. To a THIRD PARTY —
 *              the person who REPORTED the content — so NT-F06/F07 is at its hardest and the payload is
 *              ids and governed enums ONLY: no `reason`, no author id, no author name, no body, and no
 *              context ids. `targetId` is a post or tool-comment id that no web route accepts today.
 *              THIS ONE WAS MISSING FROM THIS LIST while the server shipped it (commit 2dab407 touched
 *              no web file), and both renderers fell through to "A decision was made about your
 *              content" — telling a reporter their own post had been moderated.
 *   kind 8 — `{toolId, weekStart}`
 */
export interface NotificationEntry {
  notificationId: string;
  kind: number;
  payload: unknown;
  createdAt: string;
  readAt: string | null;
}

export interface NotificationFeed {
  notifications: NotificationEntry[];
  unreadCount: number;
}

/**
 * One opt-out row. GET /api/me/notification-settings returns kinds 3, 6 AND 8 (db/notification.ts
 * `getNotificationSettings`), so 8 belongs in this union — it was missing, the mirror trap in
 * miniature. NT-F10 also makes kind 2 opt-out-able, but kind 2 is unbuilt so no row is ever returned
 * for it.
 *
 * THE GET AND PUT HALVES NOW MATCH, and this note used to say they did not: it recorded "kind 8 can be
 * READ but not yet toggled" as though it were the design, and http/routes/me.ts has since derived its
 * `:kind` enum from `setNotificationSetting`'s own signature — so PUT accepts '3' | '6' | '8' and the
 * route can no longer drift from the writer. This file still has no PUT caller, so nothing in the web
 * toggles anything yet; that is a missing screen, not a missing endpoint.
 */
export interface NotificationSetting {
  kind: 3 | 6 | 8;
  enabled: boolean;
}

export async function getNotifications(): Promise<ApiResult<NotificationFeed>> {
  return apiGet<NotificationFeed>('/api/notifications');
}

export async function markNotificationRead(notificationId: string): Promise<boolean> {
  const res = await apiPost<never>(`/api/notifications/${encodeURIComponent(notificationId)}/read`);
  return res.kind === 'ok';
}

export async function markAllNotificationsRead(): Promise<boolean> {
  const res = await apiPost<never>('/api/notifications/read-all');
  return res.kind === 'ok';
}

/**
 * Permanently delete every one of the caller's own notifications.
 *
 * `{ok: true}`, not `never`: routes/notifications.ts answers this one with a JSON body while the two
 * read endpoints above answer 204, and declaring `never` here was a mirror type asserting something the
 * server does not do. Harmless today because only `res.kind` is read — but this file is a hand-written
 * mirror of the wire, and a mirror that lies about a body is how a field gets silently dropped later.
 */
export async function clearAllNotifications(): Promise<boolean> {
  const res = await apiDelete<{ ok: true }>('/api/notifications');
  return res.kind === 'ok';
}

export async function getNotificationSettings(): Promise<
  ApiResult<{ settings: NotificationSetting[] }>
> {
  return apiGet<{ settings: NotificationSetting[] }>('/api/me/notification-settings');
}
